import type {
  ComputedInventoryStats,
  InventoryItem,
  InventoryPallet,
  InventoryResponse,
  InventoryStateStats,
} from '@admin/components/inventory/types';

const hasOpenOrResolvedIncidents = (item: InventoryItem) =>
  (item.open_incidents_count ?? 0) > 0 || (item.resolved_incidents_count ?? 0) > 0;

const hasOpenDelayedIncidents = (item: InventoryItem) => !!item.delayed;

const itemMatchesState = (item: InventoryItem, stateFilter: string) => {
  if (stateFilter === 'incidents') return hasOpenOrResolvedIncidents(item);
  if (stateFilter === 'delayed') return hasOpenDelayedIncidents(item);
  return item.state === stateFilter;
};

const emptyStat = (): InventoryStateStats => ({
  items: { count: 0, cuft: 0 },
  pallets: { count: 0 },
});

export const linkItemGroups = (inventory: InventoryResponse) => {
  const linkItem = (item: InventoryItem) => {
    if (item.group_id) {
      item.group = inventory.item_groups.find((group) => group.id === item.group_id);
    }
  };

  inventory.other.forEach(linkItem);
  inventory.pallets.forEach((pallet) => pallet.items.forEach(linkItem));
};

export const computeInventoryStats = (inventory: InventoryResponse): ComputedInventoryStats => {
  const stats: Record<string, InventoryStateStats> = {};
  const aggregate: InventoryStateStats = {
    items: { count: inventory.stats.items.count, cuft: 0 },
    pallets: { count: inventory.stats.pallets.count },
  };
  const groupIdsCuftAccountedFor: Record<string, Record<number, boolean>> = {};
  const groupIdsAggregateCuftAccountedFor: Record<number, boolean> = {};

  const stat = (state: string) => {
    if (!stats[state]) stats[state] = emptyStat();
    return stats[state];
  };

  const addCuftToStats = (item: InventoryItem) => {
    if (item.latest_open_incident_kind === 'duplicate') return;

    if (!groupIdsCuftAccountedFor[item.state]) {
      groupIdsCuftAccountedFor[item.state] = {};
    }

    let itemCuft = 0;
    if (item.group_id && item.group) {
      if (!groupIdsCuftAccountedFor[item.state][item.group_id]) {
        groupIdsCuftAccountedFor[item.state][item.group_id] = true;
        itemCuft = parseFloat(String((item.group as { cuft?: number }).cuft ?? item.cuft ?? 0));
      }
    } else {
      itemCuft = parseFloat(String(item.cuft ?? 0));
    }

    if (!Number.isNaN(itemCuft)) {
      stat(item.state).items.cuft += itemCuft;
      if (!groupIdsAggregateCuftAccountedFor[item.group_id ?? 0] || !item.group_id) {
        groupIdsAggregateCuftAccountedFor[item.group_id ?? 0] = true;
        aggregate.items.cuft += itemCuft;
      }
    }
  };

  const setup = (item: InventoryItem) => {
    stat(item.state).items.count += 1;
    addCuftToStats(item);
    if (hasOpenOrResolvedIncidents(item)) stat('incidents').items.count += 1;
    if (hasOpenDelayedIncidents(item)) stat('delayed').items.count += 1;
  };

  inventory.other.forEach(setup);
  inventory.pallets.forEach((pallet) => {
    const states = Array.from(new Set(pallet.items.map((item) => item.state)));
    pallet.items.forEach(setup);
    states.forEach((state) => {
      stat(state).pallets.count += 1;
    });
  });

  return {
    stats,
    aggregate,
    states: Object.keys(stats).sort(),
  };
};

export const filterInventoryByState = (
  inventory: InventoryResponse,
  stateFilter?: string,
): { pallets: InventoryPallet[]; other: InventoryItem[] } => {
  if (!stateFilter) {
    return { pallets: inventory.pallets, other: inventory.other };
  }

  return {
    pallets: inventory.pallets
      .map((pallet) => ({
        ...pallet,
        items: pallet.items.filter((item) => itemMatchesState(item, stateFilter)),
      }))
      .filter((pallet) => pallet.items.length > 0),
    other: inventory.other.filter((item) => itemMatchesState(item, stateFilter)),
  };
};

export const activeStats = (computed: ComputedInventoryStats, stateFilter?: string): InventoryStateStats =>
  (stateFilter ? computed.stats[stateFilter] : computed.aggregate) ?? emptyStat();
